import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { DayWindow, Severity, UnresolvedReferencesDetails } from "@starsach/shared-types";
import { firstPollUnderWay } from "@starsach/shared-types";
import { useFormat } from "../lib/useFormat";
import { Hint } from "./Hint";

/*
 * The shared surface language, in the direction of tableau.com: white
 * surfaces lifted off a light canvas by a soft shadow and a hairline ring
 * rather than a hard border, rounded a little more, given more room; small
 * uppercase labels in a muted gray; one blue for the primary action.
 */
export const surface = "rounded-xl bg-white shadow-sm ring-1 ring-neutral-200/80 dark:bg-neutral-900 dark:ring-neutral-800";
export const btnPrimary = "inline-flex min-h-9 items-center justify-center rounded-lg bg-accent px-4 text-sm font-medium text-white transition-colors hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-50";
export const btnSecondary = "inline-flex min-h-9 items-center justify-center rounded-lg border border-[#8A8A8A] bg-white px-4 text-sm font-medium text-ink transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#8A8A8A] dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800";
/**
 * A control's edge is how it is found (WCAG 2.1 SC 1.4.11, #620): 3:1 or more against the white or dark surface it sits on.
 * axe checks text contrast only, so theme-contrast.test.ts computes these. Neutral-300/700 were about 1.5:1.
 */
export const FIELD_BORDER = { light: "#8A8A8A", dark: "#8A8A8A" } as const;
export const inputClass = "mt-1 min-h-9 w-full rounded-lg border border-[#8A8A8A] bg-white px-3 text-sm text-neutral-900 shadow-sm placeholder:text-neutral-500 focus:border-accent dark:border-[#8A8A8A] dark:bg-neutral-900 dark:text-neutral-100";
export const label = "text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400";

/** A version is shown as `v0.1.0`, never bare `0.1.0` - so it reads as a version and not a malformed address. */
export const vtag = (version: string | undefined) => (version ? `v${version}` : "unknown");

/**
 * The element a page scrolls in: the nearest ancestor marked
 * `data-scroll-root`, else the document. Starsach's shell scrolls the
 * document today (index.css "Frozen bands"); anything that listens to or
 * measures the scroll goes through this, so a shell that later scrolls its
 * <main> is one attribute, not a hunt for every `window.scrollY`.
 */
export function scrollRootOf(el: Element | null): HTMLElement {
  return (el?.closest("[data-scroll-root]") as HTMLElement | null) ?? document.documentElement;
}

/**
 * The page's title band. From lg up it stays at the top while the page
 * scrolls, painting the canvas so what passes under it is hidden, and
 * publishes its height as --page-bar-height so a list's own bands can stack
 * beneath it (index.css). One per page: the last to mount owns the value.
 */
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--page-bar-height", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--page-bar-height");
    };
  }, []);
  return (
    <header ref={ref} data-page-bar="" className="z-20 mb-8 flex flex-wrap items-end justify-between gap-4 bg-canvas pb-4 pt-2 lg:sticky lg:top-0 dark:bg-neutral-950">
      <div>
        <h1 className="text-3xl font-semibold">{title}</h1>
        {subtitle && <p className="mt-2 text-base text-neutral-600 dark:text-neutral-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex max-w-full flex-wrap items-center gap-3">{actions}</div>}
    </header>
  );
}

export function Card({ title, children, className = "" }: { title?: ReactNode; children: ReactNode; className?: string }) {
  // min-w-0: in a grid or flex track a card would otherwise grow to its widest table and push the page sideways (#416).
  return (
    <section className={`min-w-0 ${surface} p-6 ${className}`}>
      {title && <h2 className={`mb-4 ${label}`}>{title}</h2>}
      {children}
    </section>
  );
}

export function StatTile({ label: caption, value, tone = "neutral", to, hint }: { label: string; value: number | string; tone?: "neutral" | "critical" | "warning" | "ok"; to?: string; hint?: ReactNode }) {
  // Tone lives in the figure, not a colored frame: a KPI reads at a glance and the surface stays quiet.
  const tones = {
    neutral: "text-ink dark:text-neutral-50",
    critical: "text-red-700 dark:text-red-300",
    warning: "text-amber-700 dark:text-amber-300",
    ok: "text-emerald-700 dark:text-emerald-300",
  };
  const body = (
    <div className={`${surface} p-5`}>
      <div className={label}>{caption}{hint}</div>
      <div className={`mt-2 text-4xl font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
  return to ? (
    <Link to={to} className="block rounded-xl transition-shadow hover:shadow-md" aria-label={`${caption}: ${value}`}>
      {body}
    </Link>
  ) : (
    body
  );
}

const severityTone: Record<Severity, string> = {
  critical: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  info: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200",
};

export function SeverityPill({ severity }: { severity?: Severity }) {
  if (!severity) return <span className="text-neutral-500">-</span>;
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase ${severityTone[severity]}`}>{severity}</span>;
}

/**
 * Enum values are lower-case on the wire (`active`, `reporting`, `pending-customer-action`)
 * and sentence case on screen. A value that already starts with a capital
 * (`ALARM`, `CASE_OPENED`), a number (`3 undelivered`) or a version tag
 * (`v0.3.0`) is shown as it is.
 */
export function sentence(value: string): string {
  if (!/^[a-z]/.test(value) || /^v\d/.test(value)) return value;
  const spaced = /^[a-z][a-z0-9_-]*$/.test(value) ? value.replace(/[_-]+/g, " ") : value;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * One pill for every status. Its text is centered; a short status (two or
 * three words) never breaks across lines, a sentence-long one may. With
 * `truncate` it holds one line and ellipsizes, the full text on hover - for
 * a status that shares a line with other things and must not push them.
 */
export function StatusPill({ status, tone, truncate = false }: { status: string; tone: "ok" | "warning" | "critical" | "neutral"; truncate?: boolean }) {
  const tones = {
    ok: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
    warning: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
    critical: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
    neutral: "bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200",
  };
  const label = sentence(status);
  const base = `inline-flex max-w-full items-center justify-center px-2.5 py-0.5 text-center align-middle text-xs font-medium ${tones[tone]}`;
  if (truncate) {
    return (
      <span className={`${base} min-w-0 whitespace-nowrap rounded-full`} title={label}>
        <span className="truncate">{label}</span>
      </span>
    );
  }
  const wrap = label.length > 24 ? "rounded-xl leading-snug" : "whitespace-nowrap rounded-full";
  return <span className={`${base} ${wrap}`}>{label}</span>;
}

export function DayWindowToggle({ value, onChange }: { value: DayWindow; onChange: (w: DayWindow) => void }) {
  const options: DayWindow[] = [7, 30, 60, 90];
  return (
    <fieldset className={`flex items-center gap-1 p-1 ${surface}`}>
      <legend className="sr-only">Day window</legend>
      {options.map((w) => (
        <button
          key={w}
          type="button"
          aria-pressed={value === w}
          onClick={() => onChange(w)}
          className={`min-h-8 min-w-11 rounded-md px-3 text-sm font-medium transition-colors ${value === w ? "bg-accent text-white" : "text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"}`}
        >
          {w}d
        </button>
      ))}
    </fieldset>
  );
}

export function LoadingState({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-3 py-8 text-neutral-600 dark:text-neutral-400">
      <span className="motion-keep h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-accent" aria-hidden="true" />
      {label}…
    </div>
  );
}

export function ErrorState({ error, onRetry, lead = "Couldn’t load this.", className = "" }: { error: unknown; onRetry?: () => void; /** The first words: a read that failed could not load; an action that failed could not be done. */ lead?: string; className?: string }) {
  const e = error as { message?: string; requestId?: string; code?: string; details?: unknown };
  const unresolved = e.code === "UNRESOLVED_REFERENCES" ? unresolvedDetails(e.details) : [];
  return (
    <div role="alert" className={`rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100 ${className}`}>
      <p className="font-medium">{lead} {unresolved.length > 0 ? "Some references do not resolve." : (e.message ?? "Unknown error.")}</p>
      {/* #473: each reference that does not resolve, where, and what fixes it - never just a count. */}
      {unresolved.map((env) => (
        <div key={env.environmentId} className="mt-2">
          <p>Unresolved in {env.environmentName ?? env.environmentId}:</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {env.unresolved.map((r) => (
              <li key={`${r.field}#${r.ref}`}>
                <code className="text-xs">{r.field}</code> names the {r.type.replaceAll("_", " ")} <q>{r.ref}</q>.{r.reason ? ` ${r.reason}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {e.requestId && <p className="mt-1 font-mono text-xs">Request ID {e.requestId}</p>}
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-3 inline-flex min-h-9 items-center rounded-lg border border-red-300 bg-white px-4 font-medium hover:bg-red-100 dark:bg-transparent dark:hover:bg-red-900">
          Try again
        </button>
      )}
    </div>
  );
}

/** The details of an `UNRESOLVED_REFERENCES` refusal, when they are the shape the API documents; otherwise nothing, and the message stands alone. */
function unresolvedDetails(details: unknown): UnresolvedReferencesDetails {
  if (!Array.isArray(details)) return [];
  return details.filter((d): d is UnresolvedReferencesDetails[number] => typeof d === "object" && d !== null && typeof d.environmentId === "string" && Array.isArray(d.unresolved) && d.unresolved.length > 0);
}

/** What this reads off whatever `useMutation` returned, so any mutation hook fits without naming its generics. */
type MutationLike = { isError: boolean; error: unknown; submittedAt: number };

/**
 * A failed action says so where it was taken
 * ([#284](https://github.com/gairm/starsach-portal/issues/284)): the one
 * way a mutation's error reaches the screen, so a request can never fail
 * silently - `mutations-surface-errors.test.ts` refuses a call site
 * without it. Takes one mutation, or several for a card with more than
 * one action; it speaks for the action taken last, so a failure clears
 * when the next action succeeds and a second failure replaces the first.
 */
export function MutationError({ of, className }: { of: MutationLike | MutationLike[]; className?: string }) {
  const latest = (Array.isArray(of) ? of : [of]).reduce((a, b) => (b.submittedAt > a.submittedAt ? b : a));
  if (!latest.isError) return null;
  return <ErrorState error={latest.error} lead="Couldn’t do that." className={className} />;
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-dashed border-neutral-300 px-6 py-12 text-center text-neutral-600 dark:border-neutral-700 dark:text-neutral-400">
      <p className="text-base font-medium text-ink dark:text-neutral-100">{title}</p>
      {hint && <p className="mt-1 text-sm">{hint}</p>}
    </div>
  );
}

/**
 * The empty state of a view the stack's pollers fill (#336). The stack
 * starts every poller once at deploy, so within thirty minutes of the
 * connectivity test an empty view is the first poll still landing; after
 * that, the view's own settled message.
 */
export function PollEmptyState({ connectedAt, what, title, hint }: { connectedAt: string | undefined; what: string; title: string; hint?: string }) {
  if (!firstPollUnderWay(connectedAt)) return <EmptyState title={title} hint={hint} />;
  return (
    <div className="rounded-xl border border-dashed border-neutral-300 px-6 py-12 text-center text-neutral-600 dark:border-neutral-700 dark:text-neutral-400">
      <p className="text-base font-medium text-ink dark:text-neutral-100">
        First poll under way
        <Hint id="polling.first-run" label="the first poll" />
      </p>
      <p className="mt-1 text-sm">The stack started polling {what} when it was deployed. Results appear here within a few minutes.</p>
    </div>
  );
}

export function NotAvailable({ what, why, because }: { what: string; why: string; because?: string }) {
  return (
    <div className="rounded-xl bg-neutral-100 p-5 text-sm dark:bg-neutral-800">
      <p className="font-medium">{what} not available{because ? ` ${because}` : ""}</p>
      <p className="mt-1 text-neutral-600 dark:text-neutral-400">{why}</p>
    </div>
  );
}

export function RelativeTime({ iso }: { iso?: string }) {
  const fmt = useFormat();
  if (!iso) return <span className="text-neutral-500">Never</span>;
  const ms = Date.now() - Date.parse(iso);
  const abs = fmt.when(iso);
  // A future instant is "in …", never "just now" (a scheduled emergency close read "just now until just now", 1 Oct).
  const span = Math.abs(ms);
  const amount = span < 3_600_000 ? `${Math.round(span / 60_000)} min` : span < 86_400_000 ? `${Math.round(span / 3_600_000)} h` : `${Math.round(span / 86_400_000)} d`;
  const rel = span < 60_000 ? "just now" : ms < 0 ? `in ${amount}` : `${amount} ago`;
  return (
    <time dateTime={iso} title={abs} className="whitespace-nowrap">
      {rel}
    </time>
  );
}

/** Everything a row knows, lowercased, so a filter term matches wherever it appears - displayed or not. */
function rowText(row: unknown): string {
  const parts: string[] = [];
  const walk = (v: unknown): void => {
    if (v === null || v === undefined) return;
    if (typeof v === "object") Object.values(v as Record<string, unknown>).forEach(walk);
    else parts.push(String(v));
  };
  walk(row);
  return parts.join(" ").toLowerCase();
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/**
 * Every list in the portal: a filter box (on unless `filter={false}`) that
 * matches the term anywhere in the row, and alphanumeric order when the
 * table names what it is keyed on (`sortBy`) - time-ordered lists leave it
 * out and keep the order they were given.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  empty,
  filter = true,
  sortBy,
  rowClass,
  scrollable = false,
}: {
  columns: Array<{ key: string; header: ReactNode; render: (row: T) => ReactNode; numeric?: boolean }>;
  rows: T[];
  rowKey: (row: T) => string;
  caption: string;
  empty: ReactNode;
  filter?: boolean;
  sortBy?: (row: T) => string | number;
  rowClass?: (row: T) => string;
  /** A table wider than its card, with nothing focusable in its cells: the scroll region itself takes focus (WCAG 2.1.1). */
  scrollable?: boolean;
}) {
  const [term, setTerm] = useState("");
  const inputId = useId();
  // The filter row's height, so the heads stack under it while both stay put (index.css "Frozen bands").
  const [bar, setBar] = useState(0);
  const barRef = useCallback((el: HTMLDivElement | null) => {
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setBar(el.offsetHeight));
    ro.observe(el);
    setBar(el.offsetHeight);
  }, []);
  if (rows.length === 0) return <>{empty}</>;
  const needle = term.trim().toLowerCase();
  let visible = needle ? rows.filter((r) => rowText(r).includes(needle)) : rows;
  if (sortBy) visible = [...visible].sort((a, b) => collator.compare(String(sortBy(a)), String(sortBy(b))));
  const showFilter = filter && rows.length > 1;
  return (
    <div style={{ "--table-bar": showFilter ? `${bar}px` : "0px" } as CSSProperties}>
      {showFilter && (
        <div ref={barRef} className="z-[2] flex flex-wrap items-center justify-between gap-3 bg-white pb-3 lg:sticky lg:top-[var(--page-bar)] dark:bg-neutral-900">
          <label htmlFor={inputId} className="sr-only">
            Filter {caption}
          </label>
          <input
            id={inputId}
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Filter"
            className="min-h-9 w-64 max-w-full rounded-lg border border-[#8A8A8A] bg-white px-3 text-sm shadow-sm placeholder:text-neutral-500 focus:border-accent dark:border-[#8A8A8A] dark:bg-neutral-900"
          />
          {needle && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400" aria-live="polite">
              {visible.length} of {rows.length}
            </span>
          )}
        </div>
      )}
      {visible.length === 0 ? (
        <p className="py-6 text-sm text-neutral-600 dark:text-neutral-400">No rows match “{term.trim()}”.</p>
      ) : (
    // Sideways scroll only below lg: a scroll container would pin the heads to itself, not the page.
    // Relative, so an sr-only label in a cell is held by the scroll box and cannot widen the page (#416).
    <div className="relative max-lg:overflow-x-auto" {...(scrollable ? { tabIndex: 0, role: "region", "aria-label": caption } : {})}>
      {/* Separate borders: a collapsed border belongs to the row, and leaves a head that stays put. */}
      <table className="w-full border-separate border-spacing-0 text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`z-[1] border-b border-neutral-200 bg-white py-3 pr-4 font-medium lg:sticky lg:top-[calc(var(--page-bar)+var(--table-bar))] dark:border-neutral-800 dark:bg-neutral-900 ${c.numeric ? "text-right" : ""}`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={rowKey(row)} className={`transition-colors hover:bg-neutral-50/80 dark:hover:bg-neutral-800/50 ${rowClass?.(row) ?? ""}`}>
              {columns.map((c) => (
                <td key={c.key} className={`border-b border-neutral-100 py-3 pr-4 align-middle dark:border-neutral-800 ${c.numeric ? "text-right tabular-nums" : ""}`}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      )}
    </div>
  );
}

const FIELDS = "input:not([type=hidden]):not(:disabled), select:not(:disabled), textarea:not(:disabled)";
const CONTROLS = "button:not(:disabled):not([data-hint]), a[href], [tabindex]:not([tabindex='-1'])";

/**
 * Where a dialog's focus starts (#420): an element marked autofocus, else
 * its first form field, else its first control that is not a hint's "?",
 * else the dialog itself. showModal() alone takes the first focusable
 * element, which was often a "?" - and that opened its popup unasked.
 */
function initialFocus(d: HTMLDialogElement): HTMLElement | null {
  const pick = (sel: string) => [...d.querySelectorAll<HTMLElement>(sel)].find((el) => !el.closest("[inert], [hidden]")) ?? null;
  return pick("[autofocus]") ?? pick(FIELDS) ?? pick(CONTROLS) ?? d;
}

/**
 * A modal for a short task started from deep in a list - the native
 * <dialog>, so it floats over the page with focus held inside it, Esc
 * closes it, and the list is exactly where it was afterwards.
 */
export function Dialog({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // Held in a ref so a parent re-rendering with a new onClose does not close and reopen the dialog, moving focus.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    // jsdom has no showModal; the open attribute keeps the tests honest about content.
    if (typeof d.showModal === "function") {
      if (!d.open) d.showModal();
    } else d.setAttribute("open", "");
    initialFocus(d)?.focus();
    const onCancel = (e: Event) => {
      e.preventDefault();
      closeRef.current();
    };
    d.addEventListener("cancel", onCancel);
    return () => {
      d.removeEventListener("cancel", onCancel);
      if (d.open && typeof d.close === "function") d.close();
    };
  }, []);
  return (
    <dialog ref={ref} aria-labelledby={titleId} tabIndex={-1} className={`m-auto w-full ${wide ? "max-w-3xl" : "max-w-lg"} rounded-2xl bg-white p-6 text-neutral-900 shadow-xl ring-1 ring-neutral-200 backdrop:bg-neutral-900/40 dark:bg-neutral-900 dark:text-neutral-100 dark:ring-neutral-800`}>
      <h2 id={titleId} className="text-lg font-semibold">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </dialog>
  );
}
